import React from 'react';
import {
  arrayUtils,
  commonUtils,
  GenericComponentType,
  moneyUtils,
  ParkingCard,
  ParkingCardProps,
  ParkingSpaceFeatureIcon,
  timeUtils,
} from '@sato/components';
import { observer } from 'mobx-react-lite';

import {
  ParkingProduct,
  ParkingSpace,
  SpecialParkingSpaceType,
} from '@models/parking.types';
import { localizationStore, t, TranslatorFn } from '@stores/localization';

type Props<T extends GenericComponentType = 'button'> = {
  parkingProduct: ParkingProduct;
} & Pick<
  ParkingCardProps<T>,
  'bottomRightContent' | 'bottomInfo' | 'className'
>;

/** Renders a card from a parking product. */
export const ParkingProductCard = observer(
  <T extends GenericComponentType = 'button'>({
    parkingProduct,
    ...rest
  }: Props<T>) => (
    <ParkingCard
      address={
        parkingProduct.parkingArea?.name ??
        parkingProduct.parkingArea?.address ??
        ''
      }
      availabilityText={
        parkingProduct.status === 'FREE'
          ? t('parking.available_now')
          : t('parking.available_soon', {
              date: parkingProduct.freeDate
                ? timeUtils.formatDate({
                    date: parkingProduct.freeDate,
                    dateFormat: timeUtils.DATE_FORMATS.DATE_SHORT,
                    locale: localizationStore.language,
                  })
                : '',
            })
      }
      body={getParkingSpaceFeaturesText(parkingProduct.parkingSpace, t)}
      priceLabel={`${moneyUtils.formatMoney({ currency: 'EUR', value: parkingProduct.rent }, localizationStore.locale.locale)} / ${t('month_unit')}`}
      title={t(`parking.${parkingProduct.parkingSpace.type}`)}
      featureIcons={getParkingSpaceFeatureIcons(parkingProduct.parkingSpace)}
      spaceNumber={
        parkingProduct.parkingSpace.specialParkingSpace ===
        'UNNUMBERED_QUOTA_PARKING_SPACE'
          ? t('parking.unnumbered')
          : parkingProduct?.parkingSpace?.labelId
      }
      {...rest}
    />
  )
);

/** We don't want to show these parking space types to the user */
const OMITTED_SPECIAL_PARKING_SPACE_TYPES: SpecialParkingSpaceType[] = [
  'QUOTA_PARKING_SPACE',
  'UNNUMBERED_QUOTA_PARKING_SPACE',
  'RESERVATION_FOR_CHARGING',
];

/** Get comma-separated parking space features */
const getParkingSpaceFeaturesText = (
  {
    heatPole,
    electricCarCharge,
    chargerPlugType,
    chargerPower,
    spaceSize,
    specialParkingSpace,
  }: ParkingSpace,
  t: TranslatorFn
): string =>
  arrayUtils.joinArrayString([
    heatPole && t(`parking.heatPole`),
    electricCarCharge && t('parking.electricCarCharge'),
    chargerPlugType && t(`parking.chargerPlugType.${chargerPlugType}`),
    chargerPower && t(`parking.chargerPowerType.${chargerPower}`),
    specialParkingSpace &&
      !OMITTED_SPECIAL_PARKING_SPACE_TYPES.includes(specialParkingSpace) &&
      t(`parking.specialParkingSpaceType.${specialParkingSpace}`),
    spaceSize,
  ]);

const getParkingSpaceFeatureIcons = (
  parkingSpace: ParkingSpace
): ParkingSpaceFeatureIcon[] =>
  (
    [
      !!parkingSpace.electricCarCharge && {
        bgColorClassName: 'bg-brand-primary-light',
        icon: 'electricFilled',
      },
      parkingSpace.specialParkingSpace === 'DISABLED_PARKING_SPACE' && {
        bgColorClassName: 'bg-white',
        icon: 'accessibleParking',
      },
    ] satisfies (ParkingSpaceFeatureIcon | false)[]
  ).filter(commonUtils.isTruthy);
